import { Controller } from 'stimulus';
import qs from 'qs';
import {
  markerIcon,
  popup,
  popupOptions,
} from '@/helpers/map';

let L;

class MapController extends Controller {
  connect() {
    this.map = null;
    this.markers = [];
    this.mapMarkers = [];
    this.mapIsOpen = false;

    if (this.hasOverlayTarget && this.overlayTarget.dataset.mapMarkers) {
      this.mapMarkers = JSON.parse(this.overlayTarget.dataset.mapMarkers);
    }

    if (window.location.search.indexOf('map=1') >= 0) {
      this.toggleMap();
    }

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && this.overlayTarget.classList.contains('is-open')) {
        this.toggleMap();
      }
    });
  }

  initializeMap() {
    MapController.loadLeaflet()
      .then((module) => {
        L = module.default;

        this.map = this.createMap();
      })
      .then(() => {
        this.addMarkers();
      });
  }

  addMarkers() {
    if (!this.mapMarkers.length) return;

    this.markers.forEach((marker) => {
      this.map.removeLayer(marker);
    });

    this.mapMarkers.forEach((data, index) => {
      if (!data.latitude || !data.longitude) return;

      const isInCart = data.is_in_cart || false;
      const hasSeen = data.has_seen || false;

      const mapMarker = L.marker([parseFloat(data.latitude), parseFloat(data.longitude)], {
        icon: markerIcon(index + 1, isInCart, hasSeen),
      })
        .bindPopup(popup(data), popupOptions)
        .openPopup()
        .addTo(this.map);

      this.markers.push(mapMarker);
    });
  }

  createMap() {
    const worldSouthWest = new L.LatLng(75, -180);
    const worldNorthEast = new L.LatLng(-75, 180);
    const bounds = new L.LatLngBounds(worldSouthWest, worldNorthEast);

    const map = L.map(this.mapTarget, {
      center: [41.8719, 12.5674],
      zoom: 6,
      zoomControl: false,
      minZoom: 3,
      maxBounds: bounds,
      maxBoundsViscosity: 0.75,
    });

    L.gridLayer.googleMutant({
      type: 'roadmap',
    }).addTo(map);

    return map;
  }

  updateMap(markers) {
    this.mapMarkers = markers;

    if (!this.map) return;

    this.addMarkers();

    const latlngs = this.markers.map((marker) => marker.getLatLng());
    const bounds = L.latLngBounds(latlngs);

    this.map.flyToBounds(bounds);
  }

  onTogglerClick() {
    this.toggleMap();
  }

  toggleMap() {
    this.mapIsOpen = !this.mapIsOpen;

    this.applyMapStateToQuery(this.mapIsOpen);
    this.changeTogglerText();
    this.repositionFilter();

    this.overlayTarget.classList.toggle('is-open');

    document.body.classList.toggle('overflow-y-hidden');

    if (!this.map) {
      this.initializeMap();
    }
  }

  changeTogglerText() {
    this.togglerTargets.forEach((togglerTarget) => {
      const { textMapOpen, textMapClosed } = togglerTarget.dataset;
      const togglerText = togglerTarget.querySelector('[data-target="map.togglerText"]');

      togglerTarget.classList.toggle('map-open');

      if (togglerText) {
        togglerTarget.querySelector('[data-target="map.togglerText"]').innerText = this.mapIsOpen
          ? textMapOpen
          : textMapClosed;
      }
    });
  }

  repositionFilter() {
    if (!this.hasFilterTarget) return;

    const $filter = $(this.filterTarget);

    if (this.hasFilterCollapseTarget) {
      $(this.filterCollapseTarget).collapse('hide');
    }

    $filter.fadeOut(300, () => {
      $filter.toggleClass('is-fixed');
    }).fadeIn(300);
  }

  static loadLeaflet() {
    return new Promise((resolve, reject) => {
      import(/* webpackChunkName: "leaflet" */ '../vendor/leaflet')
        .then(resolve)
        .catch(reject);
    });
  }

  applyMapStateToQuery(mapIsOpen) {
    // Parse query params into an object,
    // e.g. ?filter=test&filter2=test => { filter: 'test', filter2: 'test' }
    const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });

    if (queryParams) {
      // Add the map state to the query params
      queryParams.map = mapIsOpen ? '1' : '0';

      // Apply the new query parameters to the URL
      window.history.replaceState(null, null, `?${qs.stringify(queryParams)}`);
    }
  }
}

MapController.targets = [
  'map',
  'toggler',
  'togglerText',
  'overlay',
  'filter',
  'filterCollapse',
];

export default MapController;
